// src/services/api/wishlist.ts
import { httpClient } from '@/services/http/client';
import {
    ApiResponse,
    PaginatedResponse,
    FrontendPaginatedResponse,
} from "@/services/types/api";
import { Product } from "@/services/types/domain";

export type WishlistItem = {
    added?: boolean;
    exists?: boolean;
    id?: number;            // opcional, depende de tu backend
    product: Product;
    added_at?: string;
};
export type AddToWishlistResult = {
    added: boolean;        // se agregó esta vez
    exists: boolean;       // ya estaba en la lista
    item?: WishlistItem;   // si tu backend devuelve el item
    message?: string;
};
export type WishlistStats = {
    total?: number;
    on_sale?: number;
    brands?: Record<string, number>;
    categories?: Record<string, number>;
    [k: string]: any;
};

export type WishlistFilters = {
    page?: number;
    per_page?: number;
    sort?: string;
};

class WishlistApi {
    private basePath = "/wishlist";

    /**
     * Adapta PaginatedResponse<T> (backend) -> FrontendPaginatedResponse<T> (frontend)
     */
    private adaptBackendResponse<T>(payload: PaginatedResponse<T>): FrontendPaginatedResponse<T> {
        const p = payload.pagination;
        const data = payload.data ?? [];
        const from = data.length ? (p.current_page - 1) * p.per_page + 1 : 0;
        const to = data.length ? Math.min(p.current_page * p.per_page, p.total) : 0;

        return {
            data,
            meta: {
                current_page: p.current_page,
                from,
                last_page: p.total_pages,
                per_page: p.per_page,
                to,
                total: p.total,
            },
            links: {
                first: "",
                last: "",
                prev: p.current_page > 1 ? "" : undefined,
                next: p.current_page < p.total_pages ? "" : undefined,
            },
        };
    }

    /**
     * Lista la wishlist del usuario (index)
     * Backend puede devolver paginado (PaginatedResponse<Product|WishlistItem>) o listado simple.
     */
    async list(
        filters: WishlistFilters = {}
    ): Promise<FrontendPaginatedResponse<Product | WishlistItem>> {
        const res = await httpClient.get<PaginatedResponse<Product | WishlistItem> | ApiResponse<(Product | WishlistItem)[]>>(
            this.basePath,
            { params: filters }
        );

        // Si viene en formato paginado del backend
        const anyData: any = res.data as any;
        if ("pagination" in anyData && "data" in anyData) {
            return this.adaptBackendResponse(anyData as PaginatedResponse<Product | WishlistItem>);
        }

        // Si viene como ApiResponse con arreglo plano
        const arr = (res.data as ApiResponse<(Product | WishlistItem)[]>).data ?? [];
        return {
            data: arr,
            meta: {
                current_page: 1,
                from: arr.length ? 1 : 0,
                last_page: 1,
                per_page: arr.length,
                to: arr.length,
                total: arr.length,
            },
            links: { first: "", last: "", prev: undefined, next: undefined },
        };
    }

    /**
     * Agrega un producto a la wishlist
     * POST /wishlist { product_id }
     */
    async add2(productId: number): Promise<WishlistItem | { message?: string }> {
        const res = await httpClient.post<ApiResponse<WishlistItem | { message?: string }>>(
            this.basePath,
            { product_id: productId }
        );
        console.log("resposne", res);
        return res.data.data;
    }
    async add(productId: number): Promise<AddToWishlistResult> {
        try {
            const res = await httpClient.post<ApiResponse<WishlistItem>>(
                this.basePath,
                { product_id: productId }
            );
            // Éxito normal
            return {
                added: true,
                exists: false,
                item: res.data.data,
                message: res.data.message,
            };
        } catch (err: any) {
            const status = err?.response?.status;
            const payload = err?.response?.data;

            // DUPLICADO → 409
            if (status === 409) {
                // tu backend manda success: false, message: "...ya está...", data: null
                return {
                    added: false,
                    exists: true,
                    item: undefined,
                    message: payload?.message || "El producto ya está en tu lista de deseos",
                };
            }

            // Otros errores → re-lanzar con mensaje amigable
            const msg =
                payload?.message ||
                payload?.error ||
                err?.message ||
                "No se pudo agregar a la lista de deseos";
            throw new Error(msg);
        }
    }
    /**
     * Agrega en bloque
     * POST /wishlist/bulk-add { product_ids: number[] }
     */
    async bulkAdd(productIds: number[]): Promise<{ added: number; duplicates?: number; message?: string }> {
        const res = await httpClient.post<ApiResponse<{ added: number; duplicates?: number; message?: string }>>(
            `${this.basePath}/bulk-add`,
            { product_ids: productIds }
        );
        return res.data.data;
    }

    /**
     * Elimina un producto de la wishlist
     * DELETE /wishlist/{productId}
     */
    async remove(productId: number): Promise<{ removed?: boolean; message?: string } | void> {
        const res = await httpClient.delete<ApiResponse<{ removed?: boolean; message?: string }>>(
            `${this.basePath}/${productId}`
        );
        // Algunos backends devuelven 204 sin body; por eso el retorno es flexible
        return (res as any)?.data?.data;
    }

    /**
     * Vacía la wishlist
     * DELETE /wishlist
     */
    async clear(): Promise<{ removed: number; message?: string }> {
        const res = await httpClient.delete<ApiResponse<{ removed: number; message?: string }>>(this.basePath);
        return res.data.data;
    }

    /**
     * Cantidad de items en wishlist
     * GET /wishlist/count
     */
    async count(): Promise<{ count: number }> {
        const res = await httpClient.get<ApiResponse<{ count: number }>>(`${this.basePath}/count`);
        return res.data.data;
    }

    /**
     * Estadísticas
     * GET /wishlist/stats
     */
    async stats(): Promise<WishlistStats> {
        const res = await httpClient.get<ApiResponse<WishlistStats>>(`${this.basePath}/stats`);
        return res.data.data;
    }

    /**
     * Productos de wishlist en oferta
     * GET /wishlist/on-sale
     * Puede venir paginado.
     */
    async onSale(filters: WishlistFilters = {}): Promise<FrontendPaginatedResponse<Product>> {
        const res = await httpClient.get<PaginatedResponse<Product> | ApiResponse<Product[]>>(
            `${this.basePath}/on-sale`,
            { params: filters }
        );

        const anyData: any = res.data as any;
        if ("pagination" in anyData && "data" in anyData) {
            return this.adaptBackendResponse(anyData as PaginatedResponse<Product>);
        }
        const arr = (res.data as ApiResponse<Product[]>).data ?? [];
        return {
            data: arr,
            meta: {
                current_page: 1,
                from: arr.length ? 1 : 0,
                last_page: 1,
                per_page: arr.length,
                to: arr.length,
                total: arr.length,
            },
            links: { first: "", last: "", prev: undefined, next: undefined },
        };
    }

    /**
     * Recomendaciones basadas en wishlist
     * GET /wishlist/recommendations
     * Puede venir paginado.
     */
    async recommendations(filters: WishlistFilters = {}): Promise<FrontendPaginatedResponse<Product>> {
        const res = await httpClient.get<PaginatedResponse<Product> | ApiResponse<Product[]>>(
            `${this.basePath}/recommendations`,
            { params: filters }
        );

        const anyData: any = res.data as any;
        if ("pagination" in anyData && "data" in anyData) {
            return this.adaptBackendResponse(anyData as PaginatedResponse<Product>);
        }
        const arr = (res.data as ApiResponse<Product[]>).data ?? [];
        return {
            data: arr,
            meta: {
                current_page: 1,
                from: arr.length ? 1 : 0,
                last_page: 1,
                per_page: arr.length,
                to: arr.length,
                total: arr.length,
            },
            links: { first: "", last: "", prev: undefined, next: undefined },
        };
    }

    /**
     * Verifica si un producto existe en la wishlist del usuario
     * GET /wishlist/check/{productId}
     */
    async check(productId: number): Promise<{ exists: boolean }> {
        const res = await httpClient.get<ApiResponse<{ exists: boolean }>>(
            `${this.basePath}/check/${productId}`
        );
        return res.data.data;
    }

    /**
     * Mover ítems de wishlist al carrito
     * POST /wishlist/move-to-cart
     * Acepta { product_id, quantity? } o { product_ids: number[] }
     */
    async moveToCart(payload: { product_id?: number; quantity?: number; product_ids?: number[] }): Promise<any> {
        const res = await httpClient.post<ApiResponse<any>>(`${this.basePath}/move-to-cart`, payload);
        return res.data.data;
    }
}

export const wishlistApi = new WishlistApi();
export default wishlistApi;
